import { domainNameForTags, getAdobeFlowName } from './utils';
import { deviceType } from '@/helpers/helpers';

export const pageLoadAnalytics = (flowName: string, pageName: string, additionalTag?: any) => {
  const utagScript = document.getElementById('id-utag');

  const tagFunction = function () {
    const analyticsData = {
      adobe_page_name: `${domainNameForTags()}|${deviceType}|account:${flowName}:${pageName}`,
      page_name: `account:${flowName}:${pageName}`,
      page_category: 'account',
      ...additionalTag
    };

    if (pageName == '') {
      analyticsData.page_name = analyticsData.page_name.slice(0, -1);
    }

    setTimeout(() => {
      window['utag'].view(analyticsData);
    }, 500);
  };

  if (utagScript) {
    utagScript.onload = function () {
      if (window['utag']) {
        tagFunction();
      }
    };
  }
  try {
    if (window['utag']) {
      tagFunction();
    }
  } catch (e) {
    return;
  }
};

export const preparePageLoadAnalytics = () => {
  window['utag_cfg_ovrd'] = window['utag_cfg_ovrd'] || {};
  window['utag_cfg_ovrd'].noview = true;
};

export const apiErrorAnalytics = (
  flow: string,
  pageTag: string,
  field_error: string,
  analyticsTagSearch?: boolean
) => {
  const linkName = analyticsTagSearch
    ? `custom:account:${flow}:${pageTag}:search:error`
    : `custom:account:${flow}:${pageTag}:error`;
  const data = {
    payload: {
      link_name: linkName,
      field_errors: `${pageTag}:${field_error}`,
      site_error: 1
    }
  };
  try {
    if (window['utag']) {
      setTimeout(() => {
        window['utag'].link(data.payload);
      }, 50);
    }
  } catch (e) {
    console.log(e);
  }
};

export const clickAnalytics = (data?: any) => {
  try {
    if (window['utag']) {
      setTimeout(() => {
        window['utag'].link(data.payload);
      }, 1);
    }
  } catch (e) {
    console.log(e);
  }
};

/**
 * Builds a map of { translatedValue -> englishValue } by pairing two
 * same-shaped objects (or multiple pairs) by their shared keys.
 *
 * Both objects must have the same keys; the translated object supplies the
 * lookup keys (what yup puts in errors.message) and the english object
 * supplies the values to emit to analytics.
 *
 * Usage: buildTranslatedToEnglishMap(
 *   PAGE_CONS.PERSONAL_INFO_VALIDATION, PERSONAL_INFO_VALIDATION,
 *   PAGE_CONS.DOB_ERROR_MSG, DOB_ERROR_MSG
 * )
 */
export const buildTranslatedToEnglishMap = (
  ...pairs: [Record<string, string>, Record<string, string>][]
): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const [translated, english] of pairs) {
    if (!translated || !english) continue;
    for (const key of Object.keys(english)) {
      const translatedVal = translated[key];
      const englishVal = english[key];
      if (typeof translatedVal === 'string' && typeof englishVal === 'string') {
        map[translatedVal] = englishVal;
      }
    }
  }
  return map;
};

export const formErrorAnalytics = (
  error_keys: any,
  errors: any,
  flow: string,
  pageTag: string,
  buttonTag: string,
  errorDesc?: string,
  englishMessageMap?: Record<string, string>
) => {
  if (error_keys.length !== 0) {
    // for button click tagging with form errors
    const data = {
      payload: {
        link_name: `custom:account:${flow}:${pageTag}:${buttonTag}`
      }
    };
    clickAnalytics(data);
    // for error tagging — always use English constants for l1 value
    if (Object.entries(errors)) {
      const field_error = error_keys.reduce(
        (field_errors_string: string, error_key: string, index) => {
          const translatedMsg = `${errors[`${error_key}`].message}`;
          const englishMsg = englishMessageMap?.[translatedMsg] ?? translatedMsg;
          return index === 0 ? englishMsg : field_errors_string + '|' + englishMsg;
        },
        ''
      );
      const analyticsData = {
        type: 'link',
        payload: {
          link_name: `custom:account:${flow}:${pageTag}:error`,
          field_errors: `${pageTag}:${field_error}`,
          site_error: 1
        }
      };
      clickAnalytics(analyticsData);
    }
  } else {
    const analyticsData = {
      type: 'link',
      payload: {
        link_name: `custom:account:${flow}:${pageTag}:error`,
        field_errors: `${tags.noMatchFound}:${errorDesc}`,
        site_error: 1
      }
    };
    clickAnalytics(analyticsData);
  }
};

export const flowErrorAnalytics = (pageTag: string, errorType: string) => {
  const adobeFlowName = getAdobeFlowName();
  const analyticsData = {
    type: 'link',
    payload: {
      link_name: `custom:account:${adobeFlowName}:${pageTag}:error`,
      field_errors: `${pageTag}:${errorType}`,
      site_error: 1
    }
  };
  clickAnalytics(analyticsData);
};

export const existingAccountFoundAnalytics = (pageTag: string) => {
  const adobeFlowName = getAdobeFlowName();
  const analyticsData = {
    type: 'link',
    payload: {
      link_name: `custom:account:${adobeFlowName}:${pageTag}:existing_account_found`
    }
  };
  clickAnalytics(analyticsData);
};

export const tags = {
  personal: 'lookup_page',
  demo: 'demographic_page',
  rx: 'rx_label_page',
  //#To-Do below tag needs to updated with actual flow name tag for self rx tie flow. 'self' is a placeholder.
  self: 'Pharmacy',
  cg: 'caregiving_access',
  h4l: 'h4l_onboarding',
  onBoarding: 'cvscare_onboarding',
  account: 'account',
  custom: 'custom',
  alreadyTiedPage: 'already_tied_page',
  alreadyExistsPage: 'existing_account',
  alreadyExistsHelpPage: 'existing_account:need_help_choosing_a_username',
  enterYourAddressPage: 'enter_your_address',
  enterYourInformationPage: 'enter_your_info',
  checkYourDobAgainPage: 'check_your_dob_again',
  search: 'search',
  cancel: 'cancel',
  continue: 'continue',
  signInRegister: 'sign_in_register',
  enterCheckYourInfoOneMoreTimePage: 'check_info_one_more_time',
  weHavenotFoundMatch: 'we_havent_found_a_match_yet',
  checkYourDobWeHaveNotFoundMatch: `check_your_dob_again:we_havent_found_a_match_yet`,
  lnIdentifyQuestionsPage: 'lexis_nexis_identity_verification_questions',
  textVerificationRequiredPage: 'text_verification_required',
  lnFailedPage: 'lexis_nexis_failed',
  callCustomerCare: 'call_customer_care',
  enteredTooManyTimesModal: 'entered_too_many_times_modal',
  call: 'call',
  tty: 'tty',
  restartRegistration: 'restart_registration',
  mfaPage: 'send_otp',
  validateMfaPage: 'validate_otp',
  verifyByText: 'verify_by_text',
  mnaPage: 'mno_page',
  answerQuestionsPage: 'answer_questions_required',
  answerThreeQuestions: 'answer_3_identity_questions',
  validateOtpPage: 'validate_otp',
  profileLockPage: 'profile_lock_page',
  technicalIssuesPage: 'technical_issues',
  tieLockoutPage: 'tie_lockout_page',
  manyIncorrectAttempts: 'many_incorrect_attempt',
  tooManyIncorrectAttempts: 'too_many_incorrect_attempts',
  notMatchRecord: 'not_match_record',
  successPage: 'success',
  noMatchFound: 'no match found',
  tryAgainLink: 'go back to try again',
  phoneVerificationPage: 'phone_verification',
  privacyPolicy: 'privacy_policy',
  termsAndConditions: 'terms_and_conditions',
  registrationBlocked: 'blocked_registration',
  selfServe: 'self_serve',
  phoneDOB: 'phone_dob_lookup',
  useRxLabel: 'use_prescription_label',
  usePersonalInfo: 'connect_using_personal_info',
  tryRxLabel: 'try_using_your_prescription_label',
  tryPersonalInfo: 'try_using_your_personal_info',
  multipleRecords: 'multiple_records'
};
